// =============
// RevenueCat

import z from "zod";

// =============
const experimentSchema = z.object({
	experiment_id: z.string(),
	experiment_variant: z.string(),
	enrolled_at_ms: z.number(),
});

const subscriberAttributeSchema = z.object({
	updated_at_ms: z.number(),
	value: z.string(),
});

/* =========================
 * Base event (shared)
 * ========================= */

const baseEventSchema = z.object({
	event_timestamp_ms: z.number(),
	product_id: z.string(),
	period_type: z.string(),
	purchased_at_ms: z.number(),
	expiration_at_ms: z.number(),
	environment: z.string(),

	entitlement_ids: z.array(z.string()),
	transaction_id: z.string(),
	original_transaction_id: z.string(),

	app_user_id: z.string(),
	original_app_user_id: z.string(),
	aliases: z.array(z.string()),

	currency: z.string(),
	price: z.number(),
	price_in_purchased_currency: z.number(),

	store: z.string(),
	takehome_percentage: z.number(),
	tax_percentage: z.number(),
	commission_percentage: z.number(),

	id: z.string(),
	experiments: z.array(experimentSchema).optional(),
});

/* =========================
 * Discriminated event types
 * ========================= */

export const initialPurchaseEventSchema = baseEventSchema.extend({
	type: z.literal("INITIAL_PURCHASE"),

	entitlement_id: z.null(),
	presented_offering_id: z.null(),
	is_family_share: z.boolean(),
	country_code: z.string(),

	subscriber_attributes: z.object({
		biteasyInstallId: subscriberAttributeSchema.optional(),
	}),

	offer_code: z.null(),
	app_id: z.string(),
});

export const renewalEventSchema = baseEventSchema.extend({
	type: z.literal("RENEWAL"),

	entitlement_id: z.null(),
	presented_offering_id: z.null(),
	is_family_share: z.boolean(),
	country_code: z.string(),
	is_trial_conversion: z.boolean(),

	subscriber_attributes: z.object({
		biteasyInstallId: subscriberAttributeSchema.optional(),
	}),

	offer_code: z.null(),
	app_id: z.string(),
});

export const cancellationEventSchema = baseEventSchema.extend({
	type: z.literal("CANCELLATION"),

	entitlement_id: z.string().nullable(),
	presented_offering_id: z.string().nullable(),
	offer_code: z.string().nullable(),
	cancel_reason: z.string(),

	subscriber_attributes: z.object({
		biteasyInstallId: subscriberAttributeSchema.optional(),
	}),
});

const uncancellationEventSchema = baseEventSchema.extend({
	type: z.literal("UNCANCELLATION"),

	entitlement_id: z.null(),
	presented_offering_id: z.string(),
	is_family_share: z.boolean(),
	country_code: z.string(),

	subscriber_attributes: z.object({
		biteasyInstallId: subscriberAttributeSchema.optional(),
	}),
	offer_code: z.null(),
	app_id: z.string(),
});

const subscriptionPauseEventSchema = baseEventSchema.extend({
	type: z.literal("SUBSCRIPTION_PAUSED"),

	entitlement_id: z.string(),
	presented_offering_id: z.string(),
	is_family_share: z.boolean(),
	country_code: z.string(),

	auto_resume_at_ms: z.number(),

	subscriber_attributes: z.object({
		biteasyInstallId: subscriberAttributeSchema.optional(),
	}),

	offer_code: z.null(),
	app_id: z.string(),
});

export const nonRenewingPurchaseEventSchema = baseEventSchema.extend({
	type: z.literal("NON_RENEWING_PURCHASE"),

	entitlement_id: z.null(),
	presented_offering_id: z.string().nullable(),
	is_family_share: z.boolean(),
	country_code: z.string(),

	subscriber_attributes: z.object({
		biteasyInstallId: subscriberAttributeSchema.optional(),
	}),

	offer_code: z.null(),
	app_id: z.string(),
});

/* =========================
 * Final webhook schema
 * ========================= */

export const RevenueCatWebhookSchema = z.object({
	event: z.discriminatedUnion("type", [
		initialPurchaseEventSchema,
		renewalEventSchema,
		cancellationEventSchema,
		uncancellationEventSchema,
		subscriptionPauseEventSchema,
		nonRenewingPurchaseEventSchema,
	]),
	api_version: z.string(),
});

export type InitialPurchaseEvent = z.infer<typeof initialPurchaseEventSchema>;
export type RenewalEvent = z.infer<typeof renewalEventSchema>;
export type NonRenewingPurchaseEvent = z.infer<
	typeof nonRenewingPurchaseEventSchema
>;
export type CancellationEvent = z.infer<typeof cancellationEventSchema>;
export type RevenueCatEvent = z.infer<typeof RevenueCatWebhookSchema>["event"];
